import { formatPhone } from '../utils/formatUtils';

// Regras da importação em massa de leads: normalização de telefone/e-mail (para achar duplicados
// mesmo com formatos diferentes), leitura das colunas da planilha e validação das linhas.

/** Só dígitos, sem o 55 do país. Vazio se não parecer um telefone (menos de 8 dígitos). */
export function normalizePhone(raw: unknown): string {
    let d = String(raw ?? '').replace(/\D/g, '');
    if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
    return d.length >= 8 ? d : '';
}

/**
 * Chave de comparação do telefone: os 8 últimos dígitos, a mesma regra dos webhooks. Ignora máscara, DDI
 * e o 9 de celular, então "(11) 99999-9999", "+55 11 9999-9999" e "5511999999999" dão a mesma chave.
 */
export function phoneSuffixKey(raw: unknown): string {
    const d = String(raw ?? '').replace(/\D/g, '');
    return d.length >= 8 ? d.slice(-8) : '';
}

/** Padrão LIKE que acha, com qualquer máscara, os telefones que contêm a chave (8 dígitos na ordem). */
export function phoneLikePattern(key: string, wildcard = '%'): string {
    return wildcard + key.split('').join(wildcard) + wildcard;
}

export function normalizeEmail(raw: unknown): string {
    return String(raw ?? '').trim().toLowerCase();
}

/** Formas em que o mesmo telefone pode estar salvo no banco (cru, só dígitos, formatado, com 55). */
export function phoneLookupVariants(raw: unknown): string[] {
    const original = String(raw ?? '').trim();
    const digits = normalizePhone(original);
    const out = new Set<string>();
    if (original) out.add(original);
    if (digits) {
        out.add(digits);
        out.add(formatPhone(digits));
        out.add(`55${digits}`);
        out.add(`+55${digits}`);
    }
    return Array.from(out);
}

export interface ImportRow {
    name: string;
    phone: string;
    email?: string;
    notes?: string;
    company_name?: string;
}

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/** Procura a coluna pela ordem de prioridade dos termos (o primeiro termo que casar vence). */
function findKey(keys: string[], terms: string[]): string | undefined {
    for (const t of terms) {
        const hit = keys.find(k => norm(k).includes(t));
        if (hit) return hit;
    }
    return undefined;
}

/**
 * Converte uma linha da planilha. "Nome"/"Cliente" é o nome do lead; "Empresa" só vira nome
 * quando não há outra coluna de nome. As demais colunas viram observações ("Coluna: valor").
 */
export function mapSpreadsheetRow(row: Record<string, unknown>): ImportRow {
    const keys = Object.keys(row);
    const nameKey = findKey(keys, ['nome', 'name', 'cliente', 'contato', 'empresa']);
    const phoneKey = findKey(keys, ['whats', 'celular', 'telefone', 'fone', 'phone', 'tel', 'cel']);
    const emailKey = findKey(keys, ['mail']);
    const noteKey = findKey(keys, ['obs', 'nota', 'info']);
    const companyKey = findKey(keys, ['empresa', 'company']);

    const val = (k?: string) => (k ? String(row[k] ?? '').trim() : '');
    const companyIsName = companyKey !== undefined && companyKey === nameKey;

    const used = new Set([nameKey, phoneKey, emailKey, noteKey, companyIsName ? undefined : companyKey].filter(Boolean) as string[]);
    const notes: string[] = [];
    if (noteKey && val(noteKey)) notes.push(val(noteKey));
    keys.forEach(k => {
        if (used.has(k)) return;
        const v = val(k);
        if (v) notes.push(`${k}: ${v}`);
    });

    return {
        name: val(nameKey),
        phone: val(phoneKey),
        email: val(emailKey) || undefined,
        notes: notes.length ? notes.join('\n') : undefined,
        company_name: companyIsName ? undefined : val(companyKey) || undefined,
    };
}

/** Motivo para a linha não poder ser importada (ou null se está ok). */
export function rowProblem(r: ImportRow): string | null {
    if (!r.name?.trim()) return 'Sem nome';
    if (!normalizePhone(r.phone) && !normalizeEmail(r.email)) return 'Sem telefone nem e-mail';
    return null;
}

/** Quebra uma lista em blocos (inserção em lotes). */
export function chunk<T>(list: T[], size: number): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
    return out;
}

/** Roda as tarefas com no máximo `limit` em paralelo, mantendo a ordem dos resultados. */
export async function mapWithConcurrency<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
    const results = new Array<R>(items.length);
    let next = 0;
    const worker = async () => {
        while (next < items.length) {
            const i = next++;
            results[i] = await fn(items[i]);
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return results;
}

export const IMPORT_TEMPLATE_HEADERS = ['Nome', 'Telefone', 'E-mail', 'Empresa', 'Observações'];
